import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaCheck, FaWordpress, FaLayerGroup, FaRocket, FaTools } from 'react-icons/fa';
import { useLanguage } from '../i18n';

const iconMap = {
    wordpress: <FaWordpress className="text-4xl text-primary-600" />,
    customize: <FaLayerGroup className="text-4xl text-primary-600" />,
    deploy: <FaRocket className="text-4xl text-primary-600" />,
    maintenance: <FaTools className="text-4xl text-primary-600" />
};

const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
};

const modalVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 40 },
    visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', damping: 25, stiffness: 300 } },
    exit: { opacity: 0, scale: 0.95, y: 20, transition: { duration: 0.2 } }
};

const ServiceModal = ({ isOpen, onClose, service }) => {
    const { t } = useLanguage();
    const dialogRef = useRef(null);

    // Close on ESC
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    // Focus trap basic
    useEffect(() => {
        if (isOpen && dialogRef.current) {
            dialogRef.current.focus();
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = ''; };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                    variants={backdropVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    aria-modal="true"
                    role="dialog"
                    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                    <motion.div
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        ref={dialogRef}
                        tabIndex={-1}
                        className="relative max-w-3xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden"
                    >
                        <div className="absolute top-3 right-3">
                            <button
                                onClick={onClose}
                                className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-100 hover:bg-primary-600 hover:text-white transition-colors"
                                aria-label={t('close')}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="p-8 md:p-10">
                            <div className="flex items-start gap-4 mb-6">
                                <div className="shrink-0">{iconMap[service?.icon]}</div>
                                <div>
                                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{service?.title}</h2>
                                    <p className="text-gray-600 leading-relaxed">{service?.longDescription || service?.description}</p>
                                </div>
                            </div>

                            {service?.extraFeatures?.length > 0 && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('more')}:</h3>
                                    <ul className="grid md:grid-cols-2 gap-3">
                                        {service.extraFeatures.map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-2">
                                                <span className="mt-1 text-primary-600"><FaCheck /></span>
                                                <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {service?.features?.length > 0 && (
                                <div className="mb-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('features')}:</h3>
                                    <ul className="grid md:grid-cols-2 gap-3">
                                        {service.features.map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-2">
                                                <span className="mt-1 text-primary-600"><FaCheck /></span>
                                                <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => {
                                        const el = document.getElementById('contact');
                                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                                        onClose();
                                    }}
                                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-4 rounded-lg transition-colors"
                                >
                                    Liên hệ tư vấn
                                </button>
                                <button
                                    onClick={onClose}
                                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-4 rounded-lg transition-colors"
                                >
                                    Đóng
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ServiceModal;
